import React, { useState, useRef, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import Webcam from 'react-webcam';
import { createWorker } from 'tesseract.js';
import { XMarkIcon, CameraIcon, QrCodeIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface CameraScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (result: string, type: 'qr' | 'ocr') => void;
  placeholder?: string;
}

type ScanMode = 'qr' | 'ocr' | 'auto';

const CameraScanner: React.FC<CameraScannerProps> = ({
  isOpen,
  onClose,
  onScan,
  placeholder = '掃描序號或QR碼'
}) => {
  const [scanMode, setScanMode] = useState<ScanMode>('auto');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastScan, setLastScan] = useState<string>('');
  const [error, setError] = useState<string>('');
  const webcamRef = useRef<Webcam>(null);
  const workerRef = useRef<any>(null);

  // Initialize OCR worker
  const initializeOCR = useCallback(async () => {
    if (!workerRef.current) {
      workerRef.current = await createWorker('eng+chi_tra', 1, {
        logger: m => console.log(m) // Optional logging
      });
      
      await workerRef.current.setParameters({
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_',
        tessedit_pageseg_mode: '6', // Uniform block of text
        tessedit_ocr_engine_mode: '1' // Neural nets LSTM only
      });
    }
    return workerRef.current;
  }, []);

  // Handle QR code detection
  const handleQRScan = useCallback((detectedCodes: any[]) => {
    if (detectedCodes && detectedCodes.length > 0) {
      const result = detectedCodes[0].rawValue;
      if (result && result !== lastScan) {
        setLastScan(result);
        playSuccessSound();
        onScan(result, 'qr');
        
        // Brief pause to show success, then close
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    }
  }, [lastScan, onScan, onClose]);

  // Handle OCR text recognition
  const handleOCRCapture = useCallback(async () => {
    if (!webcamRef.current) return;
    
    setIsProcessing(true);
    setError('');
    
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error('無法擷取圖像');
      }

      const worker = await initializeOCR();
      const { data: { text } } = await worker.recognize(imageSrc);
      
      // Extract serial number patterns (alphanumeric with possible hyphens/underscores)
      const serialPattern = /[A-Za-z0-9\-_]{4,}/g;
      const matches = text.match(serialPattern);
      
      if (matches && matches.length > 0) {
        const longestMatch = matches.reduce((a: string, b: string) => a.length > b.length ? a : b);
        
        if (longestMatch && longestMatch !== lastScan) {
          setLastScan(longestMatch);
          playSuccessSound();
          onScan(longestMatch.trim(), 'ocr');
          
          setTimeout(() => {
            onClose();
          }, 1000);
        } else {
          setError('未檢測到有效的序號格式');
        }
      } else {
        setError('未檢測到文字，請調整角度或光線');
      }
    } catch (err) {
      console.error('OCR Error:', err);
      setError('文字識別失敗，請重試');
    } finally {
      setIsProcessing(false);
    }
  }, [initializeOCR, lastScan, onScan, onClose]);

  // Play success sound
  const playSuccessSound = () => {
    const audio = new Audio();
    audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAMAABI+AAACAQ=='; // Short beep
    audio.play().catch(() => {}); // Ignore audio errors
  };

  // Auto scan mode - tries QR first, falls back to OCR
  const handleAutoCapture = useCallback(async () => {
    // Give QR scanner a moment to detect
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // If no QR detected, try OCR
    if (!lastScan) {
      await handleOCRCapture();
    }
  }, [lastScan, handleOCRCapture]);

  // Cleanup OCR worker on unmount
  React.useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setLastScan('');
      setError('');
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            掃描器 - {scanMode === 'qr' ? 'QR碼模式' : scanMode === 'ocr' ? '文字識別' : '自動模式'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Mode Selection */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex space-x-2">
            <button
              onClick={() => setScanMode('auto')}
              className={`flex items-center px-3 py-2 rounded text-sm font-medium ${
                scanMode === 'auto'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <CameraIcon className="h-4 w-4 mr-1" />
              自動
            </button>
            <button
              onClick={() => setScanMode('qr')}
              className={`flex items-center px-3 py-2 rounded text-sm font-medium ${
                scanMode === 'qr'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <QrCodeIcon className="h-4 w-4 mr-1" />
              QR碼
            </button>
            <button
              onClick={() => setScanMode('ocr')}
              className={`flex items-center px-3 py-2 rounded text-sm font-medium ${
                scanMode === 'ocr'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DocumentTextIcon className="h-4 w-4 mr-1" />
              文字識別
            </button>
          </div>
        </div>

        {/* Scanner Area */}
        <div className="relative bg-black" style={{ height: '300px' }}>
          {scanMode === 'qr' || scanMode === 'auto' ? (
            <Scanner
              onScan={handleQRScan}
              allowMultiple={false}
              scanDelay={300}
              onError={(error: any) => {
                console.warn('QR Scanner Error:', error);
                // Don't show error for QR scanner as it's expected when no QR code is present
              }}
              constraints={{
                width: 1280,
                height: 720,
                facingMode: 'environment'
              }}
              styles={{
                container: { width: '100%', height: '100%' },
                video: { width: '100%', height: '100%', objectFit: 'cover' }
              }}
            />
          ) : (
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 1280,
                height: 720,
                facingMode: 'environment' // Use back camera on mobile
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}

          {/* Scanning overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="border-2 border-white opacity-50" style={{
              width: '200px',
              height: '200px',
              borderRadius: '8px'
            }}>
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
            </div>
          </div>

          {/* Processing indicator */}
          {isProcessing && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-white px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm font-medium">處理中...</span>
                </div>
              </div>
            </div>
          )}

          {/* Success indicator */}
          {lastScan && (
            <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
              <div className="bg-green-600 text-white px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">掃描成功: {lastScan}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50">
          {scanMode === 'ocr' && (
            <button
              onClick={handleOCRCapture}
              disabled={isProcessing}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg mb-3"
            >
              {isProcessing ? '識別中...' : '拍照識別文字'}
            </button>
          )}
          
          {scanMode === 'auto' && (
            <button
              onClick={handleAutoCapture}
              disabled={isProcessing}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-4 rounded-lg mb-3"
            >
              {isProcessing ? '掃描中...' : '自動掃描'}
            </button>
          )}

          <p className="text-xs text-gray-500 text-center">
            {scanMode === 'qr' && 'QR碼會自動識別，請將QR碼對準中央框架'}
            {scanMode === 'ocr' && '點擊「拍照識別文字」來識別文字中的序號'}
            {scanMode === 'auto' && 'QR碼自動識別，或點擊「自動掃描」進行文字識別'}
          </p>
          
          {error && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraScanner;